import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import { PlayArrow as PlayArrowIcon } from "@mui/icons-material";
import api from "../../api/client";

// Basic theme colors
const C = {
  primary: "#1c97e3",
  primaryDark: "#1565c0",
  ink: "#1a2b3c",
  muted: "#7b8794",
  line: "#e3e9f0",
  bg: "#f4f7fb",
  green: "#16a34a",
  greenBg: "#dcfce7",
  red: "#d32f2f",
  redBg: "#fee2e2",
};

export default function CodingTool({
  codingData,
  initialAnswer,
  onSave,
  disabled,
  userId,
  assessmentId,
  attemptId,
  questionId,
}) {
  const allowedLanguages = codingData?.allowed_languages || [];
  const testCases = codingData?.sample_test_cases || [];

  // Parse initial answer if it exists
  let savedCode = codingData?.starter_code || "";
  let savedLangId = codingData?.default_language_id || (allowedLanguages[0]?.id);

  if (initialAnswer) {
    try {
      const parsed = JSON.parse(initialAnswer);
      if (parsed.code) savedCode = parsed.code;
      if (parsed.language_id) savedLangId = parsed.language_id;
    } catch (e) {
      savedCode = initialAnswer;
    }
  }

  const initialResults = codingData?.testcase_results?.length
    ? codingData.testcase_results.map((tr) => ({
        id: tr.test_case_id,
        passed: String(tr.testcase).toUpperCase() === "PASS",
        output: tr.output,
        error: String(tr.testcase).toUpperCase() === "FAIL",
      }))
    : null;

  const [languageId, setLanguageId] = useState(savedLangId);
  const [activeTab, setActiveTab] = useState(initialResults ? 1 : 0);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(initialResults);

  // Use a ref for the code to prevent cursor jumping on re-renders
  const codeRef = useRef(savedCode);

  const handleChange = (val) => {
    codeRef.current = val;
    onSave(JSON.stringify({ code: val, language_id: languageId }), languageId);
  };

  const handleLanguageChange = (e) => {
    const newLangId = e.target.value;
    setLanguageId(newLangId);
    onSave(JSON.stringify({ code: codeRef.current, language_id: newLangId }), newLangId);
  };

  const getMonacoLanguage = (langId) => {
    const lang = allowedLanguages.find((l) => l.id === langId);
    if (!lang) return "plaintext";
    const name = lang.name.toLowerCase();
    if (name.includes("python")) return "python";
    if (name.includes("java") && !name.includes("javascript")) return "java";
    if (name.includes("c++") || name.includes("cpp")) return "cpp";
    if (name.includes("c#") || name.includes("csharp")) return "csharp";
    if (name.includes("javascript")) return "javascript";
    return "plaintext";
  };

  const handleRunTests = async () => {
    console.log("userId", userId);

    if (disabled || running) return;
    setRunning(true);
    setResults([]);
    setActiveTab(1);

    for (const tc of testCases) {
      let resultForThisCase = null;
      try {
        const payload = {
          user_id: userId,
          assessment_id: assessmentId,
          attempt_id: attemptId,
          question_id: questionId,
          language_id: languageId,
          source_code: codeRef.current,
          sample_test_case_id: tc.id,
          sample_test_case: tc,
          run_mode: "sample_test_case",
        };

        const res = await api.post("/AssessmentCodingRun", payload);
        const data = res.data;

        if (data?.status === "success" && data.run) {
          resultForThisCase = {
            id: tc.id,
            passed: data.run.passed === true,
            output: data.run.stdout || data.run.stderr || data.run.compile_output || data.run.message || "",
            error: data.run.stderr || data.run.compile_output,
          };
        } else {
          resultForThisCase = {
            id: tc.id,
            passed: false,
            output: data?.message || "Execution failed",
            error: true,
          };
        }
      } catch (err) {
        resultForThisCase = {
          id: tc.id,
          passed: false,
          output: err?.response?.data?.message || err.message || "Network Error",
          error: true,
        };
      }

      // Update UI sequentially after each API call completes
      setResults((prev) => [...(prev || []), resultForThisCase]);
    }

    setRunning(false);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: "8px", overflow: "hidden", mt: 2, display: "flex", flexDirection: "column", border: `1px solid ${C.line}` }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, backgroundColor: "#fbfdff", borderBottom: `1px solid ${C.line}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: C.ink }}>Language:</Typography>
          <Select
            size="small"
            value={languageId}
            onChange={handleLanguageChange}
            disabled={disabled}
            sx={{ minWidth: 150, height: 32, fontSize: 14, backgroundColor: "#fff" }}
          >
            {allowedLanguages.map((lang) => (
              <MenuItem key={lang.id} value={lang.id} sx={{ fontSize: 14 }}>
                {lang.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleRunTests}
          disabled={disabled || running}
          sx={{
            backgroundColor: C.green,
            "&:hover": { backgroundColor: "#15803d" },
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "6px",
            boxShadow: "none",
          }}
        >
          {running ? "Running..." : "Run Code"}
        </Button>
      </Box>

      {/* Editor & Test Cases Split */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, minHeight: 450 }}>
        {/* Monaco Editor */}
        <Box sx={{ flex: { xs: "none", lg: 7 }, minHeight: { xs: 300, lg: "auto" }, borderRight: { lg: `1px solid ${C.line}` }, borderBottom: { xs: `1px solid ${C.line}`, lg: "none" } }}>
          <Editor
            height="100%"
            language={getMonacoLanguage(languageId)}
            defaultValue={savedCode}
            onChange={handleChange}
            theme="vs-light"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: "on",
              scrollBeyondLastLine: false,
              readOnly: disabled,
              padding: { top: 12 },
            }}
          />
        </Box>

        {/* Test Cases Panel */}
        <Box sx={{ flex: { xs: "none", lg: 5 }, display: "flex", flexDirection: "column", backgroundColor: "#fff", width: { lg: "40%" } }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ minHeight: 40, borderBottom: `1px solid ${C.line}`, "& .MuiTab-root": { minHeight: 40, py: 0, textTransform: "none", fontWeight: 600, fontSize: 13 } }}
          >
            <Tab label="Test Cases" />
            <Tab label="Run Results" />
          </Tabs>

          <Box sx={{ flex: 1, overflowY: "auto", p: 2, maxHeight: 400 }}>
            {activeTab === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {testCases.map((tc, idx) => (
                  <Box key={tc.id} sx={{ backgroundColor: C.bg, p: 1.5, borderRadius: "6px" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1, color: C.primaryDark }}>
                      Case {idx + 1}: {tc.label}
                    </Typography>

                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.5 }}>Input (stdin):</Typography>
                    <Box sx={{ backgroundColor: "#fff", p: 1, borderRadius: "4px", border: `1px solid ${C.line}`, mb: 1.5, fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {tc.stdin || "(empty)"}
                    </Box>

                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.5 }}>Expected Output:</Typography>
                    <Box sx={{ backgroundColor: "#fff", p: 1, borderRadius: "4px", border: `1px solid ${C.line}`, fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {tc.expected_output}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!results && !running && (
                  <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", mt: 4 }}>
                    Click "Run Code" to see results here.
                  </Typography>
                )}
                {running && !results?.length && (
                  <Typography sx={{ fontSize: 13, color: C.muted, textAlign: "center", mt: 4 }}>
                    Running test cases...
                  </Typography>
                )}
                {results && results.map((r, idx) => {
                  const tc = testCases.find((t) => t.id === r.id);
                  return (
                    <Box key={r.id} sx={{ backgroundColor: r.passed ? C.greenBg : C.redBg, p: 1.5, borderRadius: "6px", border: `1px solid ${r.passed ? "#bbf7d0" : "#fecaca"}` }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: r.passed ? "#166534" : "#991b1b" }}>
                          Case {idx + 1}: {tc?.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: 12, color: r.passed ? C.green : C.red }}>
                          {r.passed ? "PASSED" : "FAILED"}
                        </Typography>
                      </Box>

                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: r.passed ? "#166534" : "#991b1b", mb: 0.5, opacity: 0.8 }}>Actual Output:</Typography>
                      <Box sx={{ backgroundColor: "#fff", p: 1, borderRadius: "4px", border: `1px solid ${r.passed ? "#86efac" : "#fca5a5"}`, fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap", color: r.error ? C.red : C.ink, wordBreak: "break-all" }}>
                        {r.output || "(no output)"}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
